import React, { useContext, useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { CartContext } from "../CartContext";
import { AuthContext } from "../AuthContext";
import Swal from "sweetalert2";
import "./Checkout.css";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const PAYMENT_BASE_URL = process.env.REACT_APP_PAYMENT_BASE_URL;

function Checkout() {
  const [isLoading, setIsLoading] = useState(false);
  const { cart, clearCart } = useContext(CartContext);
  const [productDetails, setProductDetails] = useState({});
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const { isLoggedIn, userInfo, authFetch, loading: authLoading } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [orderPlacedSuccessfully, setOrderPlacedSuccessfully] = useState(false);

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [unit, setUnit] = useState("");
  const [shippingAddress1, setShippingAddress1] = useState("");
  const [shippingAddress2, setShippingAddress2] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zipcode, setZipcode] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isPickup, setIsPickup] = useState(false);
  const [billingUnitBlock, setBillingUnitBlock] = useState("");
  const [billingAddress1, setBillingAddress1] = useState("");
  const [billingAddress2, setBillingAddress2] = useState("");
  const [billingCity, setBillingCity] = useState("");
  const [billingState, setBillingState] = useState("");
  const [billingZipcode, setBillingZipcode] = useState("");
  const [shippingContactName, setShippingContactName] = useState("");
  const [shippingContactNumber, setShippingContactNumber] = useState("");
  const [isBillingSameAsShipping, setIsBillingSameAsShipping] = useState(true);
  const [billingPhoneNumber, setBillingPhoneNumber] = useState("");
  const [orderComment, setOrderComment] = useState("");
  const [agreeTnC, setAgreeTnC] = useState(false);
  const [agreePrivacy, setAgreePrivacy] = useState(false);
  const [billingName, setBillingName] = useState("");
  const [eInvoiceRequested, setEInvoiceRequested] = useState(true);
  const [eInvoiceBuyerType, setEInvoiceBuyerType] = useState("Individual");
  const [eInvoiceTIN, setEInvoiceTIN] = useState("");
  const [eInvoiceBRN, setEInvoiceBRN] = useState("");
  const [eInvoiceSSTNumber, setEInvoiceSSTNumber] = useState("");
  const [eInvoiceName, setEInvoiceName] = useState("");
  const [eInvoiceEmail, setEInvoiceEmail] = useState("");
  const [eInvoicePhone, setEInvoicePhone] = useState("");
  const [eInvoiceAddress, setEInvoiceAddress] = useState("");
  const userId = userInfo?.userId;
  const [promoMap, setPromoMap] = useState({});

  const [voucherCode, setVoucherCode] = useState("");
  const [voucherError, setVoucherError] = useState("");
  const [appliedVoucher, setAppliedVoucher] = useState(null);
  const [discountAmount, setDiscountAmount] = useState(0);
  const [stockValidationErrors, setStockValidationErrors] = useState([]);
  const [priceValidationErrors, setPriceValidationErrors] = useState([]);
  const [moqValidationErrors, setMoqValidationErrors] = useState([]);

  const [pointsBalance, setPointsBalance] = useState(0);
  const [pointsLoading, setPointsLoading] = useState(false);
  const [cartCompositionError, setCartCompositionError] = useState("");
  const [pointsValidationErrors, setPointsValidationErrors] = useState([]);

  const malaysiaStates = [
    "Johor",
    "Kedah",
    "Kelantan",
    "Melaka",
    "Negeri Sembilan",
    "Pahang",
    "Penang",
    "Perak",
    "Perlis",
    "Sabah",
    "Sarawak",
    "Selangor",
    "Terengganu",
    "Kuala Lumpur",
  ];

  useEffect(() => {
    if (authLoading) return;

    if (!isLoggedIn || !userInfo) {
      Swal.fire({
        title: 'Login Required',
        text: 'You need to be logged in to access the checkout page.',
        icon: 'warning',
        confirmButtonColor: '#0288d1',
        confirmButtonText: 'Go to Login'
      }).then(() => {
        navigate('/login', {
          state: {
            from: location.pathname,
            message: 'Please login to complete your checkout'
          }
        });
      });
      return;
    }

    if (cart.length === 0 && !orderPlacedSuccessfully) {
      Swal.fire({
        title: 'Empty Cart',
        text: 'Your cart is empty. Please add items to your cart before checking out.',
        icon: 'info',
        confirmButtonColor: '#0288d1',
        confirmButtonText: 'Continue Shopping'
      }).then(() => {
        navigate('/category');
      });
      return;
    }
  }, [authLoading, isLoggedIn, userInfo, cart.length, navigate, location.pathname, orderPlacedSuccessfully]);

  useEffect(() => {
    if (userInfo) {
      setEmail(userInfo.email || "");
      setName(userInfo.username || "");
      setShippingAddress1(userInfo.address || "");
      setCity(userInfo.city || "");
      setState(userInfo.state || "");
      setZipcode(userInfo.zipCode || "");
      setPhoneNumber(userInfo.phoneNumber || "");
    }
  }, [userInfo]);

  useEffect(() => {
    if (isPickup) {
      setIsBillingSameAsShipping(false);
    }
  }, [isPickup]);

  const fetchPointsBalance = async () => {
    if (!userId || !userInfo) return;

    try {
      setPointsLoading(true);
      const response = await authFetch(`${API_BASE_URL}/api/Points/balance`);
      if (response.ok) {
        const data = await response.json();
        setPointsBalance(data.balance || 0);
      } else if (response.status === 404) {
        setPointsBalance(0);
      } else {
        console.error('Failed to fetch points balance');
        setPointsBalance(0);
      }
    } catch (error) {
      console.error('Error fetching points balance:', error);
      setPointsBalance(0);
    } finally {
      setPointsLoading(false);
    }
  };

  const separateCartItems = () => {
    const sellingItems = cart.filter(item => !item.isRedeemable);
    const redeemableItems = cart.filter(item => item.isRedeemable);
    return { sellingItems, redeemableItems };
  };

  // Active promotion for a cart line, if any. A promotion may target a specific
  // variant (keyed v<variantId>) or the product's base price (keyed p<productId>).
  const getPromoFor = (item) => {
    if (item.isRedeemable) return null;
    // A promotion item already holds its promotion price - never re-map it
    if (item.promotionProductId) return null;
    const key = item.selectedVariant?.variantId
      ? `v${item.selectedVariant.variantId}`
      : `p${item.productID}`;
    return promoMap[key] ?? null;
  };

  // The price actually charged for a cart line
  const getEffectivePrice = (item) => getPromoFor(item) ?? item.price;

  const calculatePointsRequired = () => {
    const { redeemableItems } = separateCartItems();
    return redeemableItems.reduce((total, item) => {
      const product = productDetails[item.productID];
      if (product?.pointsRequired) {
        return total + (product.pointsRequired * item.quantity);
      }
      return total;
    }, 0);
  };

  const validateCartComposition = () => {
    const { sellingItems, redeemableItems } = separateCartItems();

    if (redeemableItems.length > 0 && sellingItems.length === 0) {
      setCartCompositionError(
        "Redeemable products require at least one regular product in your cart. Please add a regular product to continue."
      );
      return false;
    }

    setCartCompositionError("");
    return true;
  };

  const validatePointsAvailability = () => {
    const { redeemableItems } = separateCartItems();
    const errors = [];

    if (redeemableItems.length === 0) {
      setPointsValidationErrors([]);
      return true;
    }

    const totalPointsRequired = calculatePointsRequired();

    if (pointsBalance < totalPointsRequired) {
      errors.push({
        type: 'insufficient_points',
        message: `Insufficient points. You need ${totalPointsRequired} points but only have ${pointsBalance} points.`,
        pointsRequired: totalPointsRequired,
        pointsAvailable: pointsBalance
      });
    }

    redeemableItems.forEach(item => {
      const product = productDetails[item.productID];
      if (product?.pointsRequired) {
        const itemPointsRequired = product.pointsRequired * item.quantity;
        if (product.pointsRequired <= 0) {
          errors.push({
            type: 'invalid_points',
            productID: item.productID,
            name: product.name,
            message: `Invalid points requirement for ${product.name}`
          });
        }
      }
    });

    setPointsValidationErrors(errors);
    return errors.length === 0;
  };

  useEffect(() => {
    const fetchUserData = async () => {
      if (userId) {
        try {
          const response = await authFetch(`${API_BASE_URL}/Users/${userId}`);
          if (response.ok) {
            const userData = await response.json();
            setEmail(userData.email || "");
            setName(userData.username || "");
            setShippingAddress1(userData.address || "");
            setCity(userData.city || "");
            setState(userData.state || "");
            setZipcode(userData.zipCode || "");
            setPhoneNumber(userData.phoneNumber || "");
          }
        } catch (error) {
          // Silent fail
        }
      }
    };

    fetchUserData();
    fetchPointsBalance();

    const fetchProductDetails = async () => {
      const details = {};
      for (const item of cart) {
        // Promotion items are not catalogue products. Their price, stock and MOQ are
        // all re-verified server side when the order is placed.
        if (item.promotionProductId) continue;

        try {
          const response = await fetch(
            `${API_BASE_URL}/products/${item.productID}`
          );
          if (response.ok) {
            const data = await response.json();
            details[item.productID] = data;
          }
        } catch (error) {
          // Silent fail
        }
      }
      setProductDetails(details);
      validateStockQuantities(details);
      validatePrices(details);
      validateMOQ(details);
    };

    fetchProductDetails();

    fetch(`${API_BASE_URL}/api/Promotions`)
      .then(r => r.ok ? r.json() : [])
      .then(promos => {
        const map = {};
        promos.forEach(p => {
          // Standalone promos are not catalogue products - they have no id to key on
          if (!p.productId && !p.variantId) return;
          map[p.variantId ? `v${p.variantId}` : `p${p.productId}`] = p.promotionPrice;
        });
        setPromoMap(map);
      })
      .catch(() => {});
  }, [userId, cart, authFetch]);

  // Run validation when cart or product details change
  useEffect(() => {
    if (Object.keys(productDetails).length > 0) {
      validateCartComposition();
      validatePointsAvailability();
    }
  }, [cart, productDetails, pointsBalance]);

  // Re-run price validation when promoMap loads (it loads async after productDetails)
  useEffect(() => {
    if (Object.keys(productDetails).length > 0) {
      validatePrices(productDetails);
    }
  }, [promoMap]);

  const validateStockQuantities = (details) => {
    const errors = [];
    const productQuantities = {};

    cart.forEach(item => {
      if (item.promotionProductId) return;   // verified server side
      if (!productQuantities[item.productID]) {
        productQuantities[item.productID] = 0;
      }

      // Stock is held on the product in BASE units. A variant consumes
      // (order qty x its BundleQuantity); a redeemable bundle consumes
      // (order qty x RedeemQuantity).
      const product = details[item.productID];
      let unitsPerItem = 1;

      if (item.isRedeemable) {
        unitsPerItem = product?.redeemQuantity > 0 ? product.redeemQuantity : 1;
      } else if (item.selectedVariant?.variantId) {
        const variant = product?.variants?.find(v => v.variantId === item.selectedVariant.variantId);
        unitsPerItem = variant?.bundleQuantity > 0 ? variant.bundleQuantity : 1;
      }

      productQuantities[item.productID] += item.quantity * unitsPerItem;
    });

    Object.keys(productQuantities).forEach(productID => {
      const product = details[productID];
      const totalRequestedQuantity = productQuantities[productID];

      if (product && product.stockQuantity < totalRequestedQuantity) {
        errors.push({
          productID: productID,
          name: product.name,
          stockQuantity: product.stockQuantity,
          requestedQuantity: totalRequestedQuantity
        });
      }
    });

    setStockValidationErrors(errors);
  };

  const validatePrices = (details) => {
    const errors = [];

    cart.forEach(item => {
      if (item.promotionProductId) return;   // verified server side
      const product = details[item.productID];
      if (!product) return;

      if (item.isRedeemable || product.isRedeemable) {
        return;
      }

      let expectedPrice;

      if (item.selectedVariant && item.selectedVariant.variantId) {
        const variant = product.variants?.find(v => v.variantId === item.selectedVariant.variantId);
        if (variant) {
          expectedPrice = variant.price;
        }
      } else {
        expectedPrice = product.price;
      }

      if (expectedPrice === null || expectedPrice === undefined) return;

      // Only flag if the cart price is HIGHER than the current price (customer would be
      // undercharged / stale price). A LOWER cart price is always acceptable — it's a promo
      // or discount, and the correct price is re-validated server-side at submission.
      if (item.price - expectedPrice > 0.01) {
        errors.push({
          productID: item.productID,
          name: product.name,
          variant: item.selectedVariant?.variantId,
          expectedPrice: expectedPrice,
          cartPrice: item.price
        });
      }
    });

    setPriceValidationErrors(errors);
  };

  const validateMOQ = (details) => {
    const errors = [];

    cart.forEach(item => {
      if (item.promotionProductId) return;   // verified server side
      const product = details[item.productID];
      if (!product) return;

      if (item.belowMOQ || item.insufficientStock) {
        return;
      }

      // MOQ is optional - only enforce when there actually is one (> 1).
      // A selected variant's MOQ takes precedence over the product's.
      const variant = item.selectedVariant?.variantId
        ? product.variants?.find(v => v.variantId === item.selectedVariant.variantId)
        : null;
      const effectiveMoq = variant?.moq || item.moq || product.moq;

      if (effectiveMoq && effectiveMoq > 1 && effectiveMoq > item.quantity) {
        errors.push({
          productID: item.productID,
          name: product.name,
          moq: effectiveMoq,
          quantity: item.quantity
        });
      }
    });

    setMoqValidationErrors(errors);
  };

  const validateVoucher = async () => {
    setVoucherError("");
    setAppliedVoucher(null);
    setDiscountAmount(0);
    setSuccess("");

    if (!voucherCode.trim()) {
      setVoucherError("Please enter a voucher code");
      return;
    }

    const normalizedVoucherCode = voucherCode.trim().toUpperCase();
    setVoucherCode(normalizedVoucherCode);

    try {
      const { sellingItems } = separateCartItems();

      if (sellingItems.length === 0) {
        setVoucherError("Vouchers can only be applied to regular products, not redeemable items.");
        return;
      }

      const toPositiveInteger = (value) => {
        const parsedValue = Number(value);
        return Number.isInteger(parsedValue) && parsedValue > 0 ? parsedValue : 0;
      };

      const cartItems = sellingItems.map(item => ({
        VariantsId: toPositiveInteger(item.selectedVariant?.variantId),
        // Standalone promotion items intentionally have no catalogue product ID.
        // Send 0 so ASP.NET can deserialize the request and the API can skip that
        // item while validating the voucher against regular catalogue products.
        ProductId: toPositiveInteger(item.productID),
        Quantity: Number(item.quantity),
        Price: Number(getEffectivePrice(item))
      }));

      const response = await authFetch(`${API_BASE_URL}/api/Orders/validate-voucher`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          VoucherCode: normalizedVoucherCode,
          CartItems: cartItems
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setAppliedVoucher(data);
        setDiscountAmount(data.discountAmount);

        let successMsg = "Voucher applied!";
        if (data.isPercentage) {
          successMsg += ` ${data.discountValue}% discount`;
        } else {
          successMsg += ` RM${data.discountValue.toFixed(2)} discount`;
        }

        if (data.discountAmount === 0) {
          successMsg += " (No eligible products in cart)";
        } else {
          successMsg += ` (Saved RM${data.discountAmount.toFixed(2)})`;
        }

        setSuccess(successMsg);
      } else {
        let errorMsg = data.message || "Invalid voucher code";

        if (errorMsg.includes("expired")) {
          errorMsg = "This voucher has expired. Please try another code.";
        } else if (errorMsg.includes("not yet active")) {
          errorMsg = "This voucher is not active yet. Please check back later.";
        } else if (errorMsg.includes("maximum usage limit")) {
          errorMsg = "This voucher has reached its usage limit. Please try another code.";
        } else if (errorMsg.includes("not applicable to any items")) {
          errorMsg = "This voucher doesn't apply to any products in your cart. Try adding eligible products.";
        } else if (errorMsg.includes("minimum spend")) {
          errorMsg = data.message;
        }

        setVoucherError(errorMsg);
      }
    } catch (error) {
      setVoucherError("Error validating voucher. Please try again.");
    }
  };

  const removeVoucher = () => {
    setVoucherCode("");
    setAppliedVoucher(null);
    setDiscountAmount(0);
    setVoucherError("");
    setSuccess("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.target;

    if (!agreeTnC || !agreePrivacy) {
      Swal.fire({
        icon: "warning",
        title: "Agreement Required",
        text: "Please agree to the Terms and Conditions and Privacy Policy before placing your order.",
      });
      return;
    }

    if (cart.length === 0) {
      Swal.fire({
        icon: "warning",
        title: "Cart Empty",
        text: "Your cart is empty. Please add items to your cart before placing an order.",
      });
      return;
    }

    if (!isPickup) {
      if (!form.shippingAddress1.value || !form.city.value ||
        !form.state.value || !form.zipcode.value) {
        Swal.fire({
          icon: "warning",
          title: "Missing Fields",
          text: "Please fill in all required shipping fields.",
        });
        return;
      }
    }

    if (!form.checkValidity()) {
      Swal.fire({
        icon: "warning",
        title: "Missing Fields",
        text: "Please fill in all required fields.",
      });
      return;
    }

    if (!validateCartComposition()) {
      setError(cartCompositionError);
      return;
    }

    if (!validatePointsAvailability()) {
      const pointsErrors = pointsValidationErrors.map(err => err.message).join('\n');
      setError(pointsErrors);

      Swal.fire({
        title: 'Points Validation Failed',
        html: `
          <div style="text-align: left;">
            ${pointsValidationErrors.map(err => `<p>${err.message}</p>`).join('')}
          </div>
        `,
        icon: 'error',
        confirmButtonColor: '#0288d1',
        confirmButtonText: 'OK'
      });

      return;
    }

    if (stockValidationErrors.length > 0) {
      const errorItems = stockValidationErrors.map(err =>
        `${err.name}`
      ).join('\n');

      setError(`Some items in your cart exceed available stock:\n${errorItems}`);

      Swal.fire({
        title: 'Insufficient Stock',
        html: `
          <div style="text-align: left;">
            <p>The following items do not have enough stock:</p>
            <ul style="list-style-type: none; padding-left: 0;">
              ${stockValidationErrors.map(err =>
          `<li><strong>${err.name}</strong></li>`
        ).join('')}
            </ul>
            <p>Please adjust the quantities in your cart before proceeding.</p>
          </div>
        `,
        icon: 'error',
        confirmButtonColor: '#0288d1',
        confirmButtonText: 'Back to Cart'
      }).then(() => {
        navigate("/cart");
      });

      return;
    }

    if (moqValidationErrors.length > 0) {
      const errorItems = moqValidationErrors.map(err =>
        `${err.name}: Minimum order quantity: ${err.moq}, Current quantity: ${err.quantity}`
      ).join('\n');

      setError(`Some items in your cart don't meet minimum order quantities:\n${errorItems}`);

      Swal.fire({
        title: 'Minimum Order Quantity Not Met',
        html: `
          <div style="text-align: left;">
            <p>The following items don't meet minimum order quantities:</p>
            <ul style="list-style-type: none; padding-left: 0;">
              ${moqValidationErrors.map(err =>
          `<li><strong>${err.name}</strong>: Minimum order quantity: ${err.moq}, Current quantity: ${err.quantity}</li>`
        ).join('')}
            </ul>
            <p>Please adjust the quantities in your cart before proceeding.</p>
          </div>
        `,
        icon: 'warning',
        confirmButtonColor: '#0288d1',
        confirmButtonText: 'Back to Cart'
      }).then(() => {
        navigate("/cart");
      });

      return;
    }

    if (priceValidationErrors.length > 0) {
      const errorItems = priceValidationErrors.map(err =>
        `${err.name}: Current price: RM${err.expectedPrice.toFixed(2)}, Cart price: RM${err.cartPrice.toFixed(2)}`
      ).join('\n');

      setError(`Some items in your cart have price discrepancies:\n${errorItems}`);

      Swal.fire({
        title: 'Price Discrepancy Detected',
        html: `
          <div style="text-align: left;">
            <p>The following items have price changes since they were added to your cart:</p>
            <ul style="list-style-type: none; padding-left: 0;">
              ${priceValidationErrors.map(err =>
          `<li><strong>${err.name}</strong>: Current price: RM${err.expectedPrice.toFixed(2)}, Cart price: RM${err.cartPrice.toFixed(2)}</li>`
        ).join('')}
            </ul>
            <p>Please refresh your cart before proceeding.</p>
          </div>
        `,
        icon: 'warning',
        confirmButtonColor: '#0288d1',
        confirmButtonText: 'Back to Cart'
      }).then(() => {
        navigate("/cart");
      });

      return;
    }

    if (!isBillingSameAsShipping) {
      if (!form.billingAddress1.value || !form.billingCity.value ||
        !form.billingState.value || !form.billingZipcode.value) {
        setError("Please fill in all required billing fields.");
        return;
      }
    }

    if (eInvoiceRequested) {
      if (!form.eInvoiceBuyerType.value || !form.eInvoiceName.value ||
          !form.eInvoiceTIN.value || !form.eInvoiceBRN.value ||
          !form.eInvoicePhone.value || !form.eInvoiceEmail.value ||
          !form.eInvoiceAddress.value) {
        Swal.fire({
          icon: "warning",
          title: "Missing Fields",
          text: "Please fill in all required E-Invoice fields.",
        });
        return;
      }
    }

    setError("");

    const fullAddress = isPickup
      ? "Pickup at store"
      : `${shippingAddress1}${shippingAddress2 ? ', ' + shippingAddress2 : ''}${unit ? ', Unit/Block: ' + unit : ''}, ${city}, ${zipcode} ${state}`;

    const fullBillingAddress = isPickup
      ? `${billingAddress1}${billingAddress2 ? ', ' + billingAddress2 : ''}${billingUnitBlock ? ', Unit/Block: ' + billingUnitBlock : ''}, ${billingCity}, ${billingZipcode} ${billingState}`
      : (isBillingSameAsShipping
        ? fullAddress
        : `${billingAddress1}${billingAddress2 ? ', ' + billingAddress2 : ''}${billingUnitBlock ? ', Unit/Block: ' + billingUnitBlock : ''}, ${billingCity}, ${billingZipcode} ${billingState}`);

    const totalPointsRequired = calculatePointsRequired();

    let confirmationHtml = `
    <div style="text-align: left; margin-bottom: 20px;">
    <p>Please confirm your order details:</p>
    <p><strong>Name:</strong><br>${name}</p>
    <p><strong>Order Type:</strong><br>${isPickup ? 'Pickup' : 'Delivery'}</p>
    ${!isPickup ? `<p><strong>Shipping Address:</strong><br>${fullAddress}</p>` : ''}
    <p><strong>Billing Address:</strong><br>${fullBillingAddress}</p>
    <p><strong>Phone Number:</strong><br>${phoneNumber}</p>
    <p><strong>Email:</strong><br>${email}</p>
    `;

    if (redeemableItems.length > 0) {
      confirmationHtml += `
        <hr style="margin: 15px 0;">
        <p><strong>Points Information:</strong></p>
        <p>Points to be deducted: <strong>${totalPointsRequired}</strong></p>
        <p>Remaining points after order: <strong>${pointsBalance - totalPointsRequired}</strong></p>
      `;
    }

    confirmationHtml += `</div>`;

    const result = await Swal.fire({
      title: 'Confirm Order',
      html: confirmationHtml,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#0288d1',
      cancelButtonColor: '#bfbbbb',
      confirmButtonText: 'Confirm Order',
      cancelButtonText: 'Edit Details'
    });

    if (!result.isConfirmed) {
      return;
    }

    setIsLoading(true);

    const orderData = {
      name: name,
      email: email,
      userID: userId || null,

      isPickup: isPickup,
      shippingContactName: isPickup ? "" : form.shippingContactName.value,
      shippingContactNumber: isPickup ? "" : form.shippingContactNumber.value,
      unitBlock: isPickup ? "" : form.unit.value,
      shippingAddress1: isPickup ? "" : form.shippingAddress1.value,
      shippingAddress2: isPickup ? "" : form.shippingAddress2.value,
      shippingCity: isPickup ? "" : form.city.value,
      shippingState: isPickup ? "" : form.state.value,
      shippingZipCode: isPickup ? "" : form.zipcode.value,

      isBillingSameAsShipping: isPickup ? false : isBillingSameAsShipping,
      billingUnitBlock: (isPickup || !isBillingSameAsShipping) ? form.billingUnitBlock.value : form.unit.value,
      billingAddress1: (isPickup || !isBillingSameAsShipping) ? form.billingAddress1.value : form.shippingAddress1.value,
      billingAddress2: (isPickup || !isBillingSameAsShipping) ? form.billingAddress2.value : form.shippingAddress2.value,
      billingCity: (isPickup || !isBillingSameAsShipping) ? form.billingCity.value : form.city.value,
      billingState: (isPickup || !isBillingSameAsShipping) ? form.billingState.value : form.state.value,
      billingZipCode: (isPickup || !isBillingSameAsShipping) ? form.billingZipcode.value : form.zipcode.value,
      // When "same as shipping", billing name/phone must mirror the SHIPPING contact
      // (contact person + contact number), not the orderer's Details name/phone.
      billingName: (isPickup || !isBillingSameAsShipping) ? form.billingName.value : form.shippingContactName.value,
      billingPhoneNumber: (isPickup || !isBillingSameAsShipping) ? form.billingPhoneNumber.value : form.shippingContactNumber.value,

      paymentMethod: "",
      orderComment: orderComment,
      phoneNumber: phoneNumber,
      voucherCode: appliedVoucher ? voucherCode : null,

      eInvoiceRequested: eInvoiceRequested,
      eInvoiceBuyerType: eInvoiceRequested ? eInvoiceBuyerType : null,
      eInvoiceTIN: eInvoiceRequested ? eInvoiceTIN : null,
      eInvoiceBRN: eInvoiceRequested ? eInvoiceBRN : null,
      eInvoiceSSTNumber: eInvoiceRequested ? eInvoiceSSTNumber : null,
      eInvoiceName: eInvoiceRequested ? eInvoiceName : null,
      eInvoiceEmail: eInvoiceRequested ? eInvoiceEmail : null,
      eInvoicePhone: eInvoiceRequested ? eInvoicePhone : null,
      eInvoiceAddress: eInvoiceRequested ? eInvoiceAddress : null,
      orderItems: cart.map((item) => {
        const product = productDetails[item.productID] || {};
        const effectivePrice = getEffectivePrice(item);
        return {
          variantsId: item.selectedVariant ? item.selectedVariant.variantId : 0,
          productId: item.productID || 0,
          promotionProductId: item.promotionProductId || null,
          productName: product.name || item.name,
          productCode: product.productCode || '',
          quantity: item.quantity,
          price: effectivePrice,
        };
      }),
    };

    try {
      const response = await authFetch(`${API_BASE_URL}/api/Orders`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(orderData),
      });

      setIsLoading(false);

      if (response.ok) {
        const result = await response.json();

        // setOrderPlacedSuccessfully(true);

        // setSuccess("Order placed successfully!");
        // clearCart();

        // let successMessage = 'Order placed successfully!';
        // if (result.pointsUsed > 0) {
        //   successMessage += ` ${result.pointsUsed} points were deducted for redeemed items.`;
        // }

        // Swal.fire({
        //   title: 'Success!',
        //   text: successMessage,
        //   icon: 'success',
        //   showCancelButton: true,
        //   confirmButtonColor: '#0288d1',
        //   cancelButtonColor: '#28a745',
        //   confirmButtonText: 'Continue Shopping',
        //   cancelButtonText: 'View Dashboard',
        //   reverseButtons: true
        // }).then((result) => {
        //   if (result.isConfirmed) {
        //     navigate("/category");
        //   } else if (result.dismiss === Swal.DismissReason.cancel) {
        //     navigate("/dashboard");
        //   }
        // });

        // Nothing to pay - a voucher covered the whole amount, or it was a points-only
        // redemption. The backend has already completed the order, so there is no gateway
        // to visit.
        if (result.requiresPayment === false) {
          setOrderPlacedSuccessfully(true);
          clearCart();
          try { localStorage.removeItem('pendingOrderId'); } catch (e) { }

          Swal.fire({
            icon: 'success',
            title: 'Order Confirmed',
            text: 'No payment was needed for this order. A confirmation email is on its way.',
            confirmButtonColor: '#0288d1',
            confirmButtonText: 'View My Orders'
          }).then(() => {
            navigate('/dashboard');
          });
          return;
        }

        Swal.fire({
          title: 'Loading',
          text: 'Please Wait. Redirect To Payment Gateway.',
          showConfirmButton: false,
          allowOutsideClick: false,
          allowEscapeKey: false
        });

        // Remember the order awaiting payment so the cart can be cleared once it is paid
        try { localStorage.setItem('pendingOrderId', String(result.orderId)); } catch (e) { }

        window.location = `${PAYMENT_BASE_URL}/payment/generate?id=${result.orderId}`;

      } else {
        if (
          response.headers.get("content-type")?.includes("application/json")
        ) {
          try {
            const errorData = await response.json();
            setError(errorData.message || "Failed to place order");
          } catch (e) {
            setError("Failed to place order");
          }
        } else {
          setError("Failed to place order");
        }
      }
    } catch (error) {
      setIsLoading(false);
      setError(`Failed to place order: ${error.message}`);
    }
  };

  if (authLoading) {
    return (
      <div className="user-checkout-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <div>
          <i className="fa fa-spinner fa-spin" style={{ fontSize: '2rem', color: '#0288d1' }}></i>
          <p style={{ marginTop: '1rem', textAlign: 'center' }}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn || !userInfo) {
    return (
      <div className="user-checkout-container">
        <div className="user-checkout-main-container">
          <div className="user-checkout-content" style={{
            backgroundColor: 'white',
            minHeight: '100vh'
          }}>
          </div>
        </div>
      </div>
    );
  }

  const { sellingItems, redeemableItems } = separateCartItems();

  const totalPrice = sellingItems.reduce((total, product) => {
    const ep = getEffectivePrice(product);
    return total + ep * product.quantity;
  }, 0);
  const totalPointsRequired = calculatePointsRequired();
  const finalPrice = totalPrice - discountAmount;

  return (
    <div className="user-checkout-container">
      <div className="user-checkout-main-container">
        <div className="user-checkout-content">
          <h2>Checkout</h2>
          {/* Checkout Form Section*/}
          <div className="user-checkout-row">
            <div className="user-checkout-col-75">
              <div className="user-checkout-form-container">
                <form onSubmit={handleSubmit} noValidate>
                  <div className="user-checkout-row">
                    <div className="user-checkout-col-50">
                      {error && (
                        <div className="user-checkout-error-message">{error}</div>
                      )}
                      {success && (
                        <div className="user-checkout-success-message">{success}</div>
                      )}

                      {cartCompositionError && (
                        <div className="user-checkout-error-message">
                          <p>{cartCompositionError}</p>
                        </div>
                      )}

                      {pointsValidationErrors.length > 0 && (
                        <div className="user-checkout-error-message">
                          <ul>
                            {pointsValidationErrors.map((err, index) => (
                              <li key={index}>{err.message}</li>
                            ))}
                          </ul>
                        </div>
                      )}

                      {stockValidationErrors.length > 0 && (
                        <div className="user-checkout-error-message">
                          <p>Some items do not have enough stock:</p>
                          <ul>
                            {stockValidationErrors.map((err, index) => (
                              <li key={index}>
                                {err.name}
                              </li>
                            ))}
                          </ul>
                          <p>Please adjust quantities in your cart before proceeding.</p>
                        </div>
                      )}

                      {priceValidationErrors.length > 0 && (
                        <div className="user-checkout-error-message">
                          <p>Price discrepancies detected:</p>
                          <ul>
                            {priceValidationErrors.map((err, index) => (
                              <li key={index}>
                                {err.name}: Current price: RM{err.expectedPrice.toFixed(2)}, Cart price: RM{err.cartPrice.toFixed(2)}
                              </li>
                            ))}
                          </ul>
                          <p>Please refresh your cart before proceeding.</p>
                        </div>
                      )}

                      {moqValidationErrors.length > 0 && (
                        <div className="user-checkout-error-message">
                          <p>Minimum order quantity not met:</p>
                          <ul>
                            {moqValidationErrors.map((err, index) => (
                              <li key={index}>
                                {err.name}: Minimum order quantity: {err.moq}, Current quantity: {err.quantity}
                              </li>
                            ))}
                          </ul>
                          <p>Please adjust quantities in your cart before proceeding.</p>
                        </div>
                      )}

                      <h3>Shipping Method</h3>
                      <div className="user-checkout-shipping-method">
                        <label className="shipping-method-label">
                          <input
                            type="radio"
                            name="shippingMethod"
                            checked={isPickup}
                            onChange={() => setIsPickup(true)}
                          />
                          <div className="shipping-method-details">
                            <span className="shipping-method-title"><i className="fas fa-store"></i> Pick-up in Store</span>
                            <span className="shipping-method-contact">Contact Us : 012-482 5199</span>
                            <span className="shipping-method-price">Free</span>
                          </div>
                        </label>
                        <label className="shipping-method-label">
                          <input
                            type="radio"
                            name="shippingMethod"
                            checked={!isPickup}
                            onChange={() => setIsPickup(false)}
                          />
                          <div className="shipping-method-details">
                            <span className="shipping-method-title"><i className="fas fa-truck"></i> Delivery Fees</span>
                            <span className="shipping-method-contact">Contact Us : 012-482 5199</span>
                            <span className="shipping-method-price">To Be Quoted Based on Location & Quantity</span>
                          </div>
                        </label>
                      </div>

                      <h3>Details</h3>
                      <div className="user-checkout-row">
                        <div className="user-checkout-col-50-1">
                          <label htmlFor="name">
                            <i className="fa fa-user"></i> Name{" "}
                            <span className="user-checkout-required">*</span>
                          </label>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            required
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                          />
                        </div>
                        <div className="user-checkout-col-50-1">
                          <label htmlFor="email">
                            <i className="fa fa-envelope"></i> Email Address{" "}
                            <span className="user-checkout-required">*</span>
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            required
                            onChange={(e) => setEmail(e.target.value)}
                            value={email}
                          />
                        </div>
                        <div className="user-checkout-col-50-1">
                          <label htmlFor="phoneNumber">
                            <i className="fa fa-phone"></i> Phone Number{" "}
                            <span className="user-checkout-required">*</span>
                          </label>
                          <input
                            type="tel"
                            pattern="[0-9]{8,12}"
                            id="phoneNumber"
                            name="phoneNumber"
                            required
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                          />
                        </div>
                      </div>



                      <h3>Billing Information</h3>
                      {!isPickup && (
                        <div className="user-checkout-billing-option">
                          <label>
                            <input
                              type="checkbox"
                              checked={isBillingSameAsShipping}
                              onChange={(e) => setIsBillingSameAsShipping(e.target.checked)}
                            />
                            Billing Address same as Shipping Address
                          </label>
                        </div>
                      )}

                      {(isPickup || !isBillingSameAsShipping) && (
                        <>
                          <div className="user-checkout-row">
                            <div className="user-checkout-col-50-1">
                              <label htmlFor="billingName">
                                <i className="fa fa-user"></i> Billing Name{" "}
                                <span className="user-checkout-required">*</span>
                              </label>
                              <input
                                type="text"
                                id="billingName"
                                name="billingName"
                                required
                                value={billingName}
                                onChange={(e) => setBillingName(e.target.value)}
                              />
                            </div>
                            <div className="user-checkout-col-50-1">
                              <label htmlFor="billingPhoneNumber">
                                <i className="fa fa-phone"></i> Billing Phone Number{" "}
                                <span className="user-checkout-required">*</span>
                              </label>
                              <input
                                type="tel"
                                pattern="[0-9]{8,12}"
                                id="billingPhoneNumber"
                                name="billingPhoneNumber"
                                required
                                value={billingPhoneNumber}
                                onChange={(e) => setBillingPhoneNumber(e.target.value)}
                              />
                            </div>
                            <div className="user-checkout-col-50-1">
                              <label htmlFor="billingUnitBlock">
                                <i className="fa fa-home"></i> Unit/Block
                              </label>
                              <input
                                type="text"
                                id="billingUnitBlock"
                                name="billingUnitBlock"
                                value={billingUnitBlock}
                                onChange={(e) => setBillingUnitBlock(e.target.value)}
                              />
                            </div>
                          </div>

                          <div className="user-checkout-row">
                            <div className="user-checkout-col-50-1">
                              <label htmlFor="billingAddress1">
                                <i className="fa fa-address-card"></i> Billing Address 1{" "}
                                <span className="user-checkout-required">*</span>
                              </label>
                              <input
                                type="text"
                                id="billingAddress1"
                                name="billingAddress1"
                                required={!isBillingSameAsShipping}
                                value={billingAddress1}
                                onChange={(e) => setBillingAddress1(e.target.value)}
                              />
                            </div>
                          </div>

                          <div className="user-checkout-row">
                            <div className="user-checkout-col-50-1">
                              <label htmlFor="billingAddress2">
                                <i className="fa fa-address-card"></i> Billing Address 2
                              </label>
                              <input
                                type="text"
                                id="billingAddress2"
                                name="billingAddress2"
                                value={billingAddress2}
                                onChange={(e) => setBillingAddress2(e.target.value)}
                              />
                            </div>
                          </div>

                          <div className="user-checkout-row">
                            <div className="user-checkout-col-50-1">
                              <label htmlFor="billingZipcode">
                                Zipcode <span className="user-checkout-required">*</span>
                              </label>
                              <input
                                type="number"
                                id="billingZipcode"
                                name="billingZipcode"
                                required={!isBillingSameAsShipping}
                                value={billingZipcode}
                                onChange={(e) => setBillingZipcode(e.target.value)}
                              />
                            </div>
                            <div className="user-checkout-col-50-1">
                              <label htmlFor="billingCity">
                                <i className="fa fa-institution"></i> City{" "}
                                <span className="user-checkout-required">*</span>
                              </label>
                              <input
                                type="text"
                                id="billingCity"
                                name="billingCity"
                                required={!isBillingSameAsShipping}
                                value={billingCity}
                                onChange={(e) => setBillingCity(e.target.value)}
                              />
                            </div>
                            <div className="user-checkout-col-50-1">
                              <label htmlFor="billingState">
                                State <span className="user-checkout-required">*</span>
                              </label>
                              <select
                                id="billingState"
                                name="billingState"
                                required={!isBillingSameAsShipping}
                                value={billingState}
                                onChange={(e) => setBillingState(e.target.value)}
                              >
                                <option value="">Select State</option>
                                {malaysiaStates.map((state) => (
                                  <option key={state} value={state}>
                                    {state}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </>
                      )}

                      {!isPickup && (
                        <>
                          <h3>Shipping Information</h3>
                          <div className="user-checkout-row">
                            <div className="user-checkout-col-50-1">
                              <label htmlFor="shippingContactName">
                                <i className="fa fa-user"></i> Contact Person Name{" "}
                                <span className="user-checkout-required">*</span>
                              </label>
                              <input
                                type="text"
                                id="shippingContactName"
                                name="shippingContactName"
                                required
                                value={shippingContactName}
                                onChange={(e) => setShippingContactName(e.target.value)}
                              />
                            </div>
                            <div className="user-checkout-col-50-1">
                              <label htmlFor="shippingContactNumber">
                                <i className="fa fa-phone"></i> Contact Number{" "}
                                <span className="user-checkout-required">*</span>
                              </label>
                              <input
                                type="tel"
                                pattern="[0-9]{8,12}"
                                id="shippingContactNumber"
                                name="shippingContactNumber"
                                required
                                value={shippingContactNumber}
                                onChange={(e) => setShippingContactNumber(e.target.value)}
                              />
                            </div>
                            <div className="user-checkout-col-50-1">
                              <label htmlFor="unit">
                                <i className="fa fa-home"></i> Unit/Block
                              </label>
                              <input
                                type="text"
                                id="unit"
                                name="unit"
                                value={unit}
                                onChange={(e) => setUnit(e.target.value)}
                              />
                            </div>
                          </div>

                          <div className="user-checkout-row">
                            <div className="user-checkout-col-50-1">
                              <label htmlFor="shippingAddress1">
                                <i className="fa fa-address-card"></i> Shipping Address 1{" "}
                                <span className="user-checkout-required">*</span>
                              </label>
                              <input
                                type="text"
                                id="shippingAddress1"
                                name="shippingAddress1"
                                required
                                value={shippingAddress1}
                                onChange={(e) => setShippingAddress1(e.target.value)}
                              />
                            </div>
                          </div>

                          <div className="user-checkout-row">
                            <div className="user-checkout-col-50-1">
                              <label htmlFor="shippingAddress2">
                                <i className="fa fa-address-card"></i> Shipping Address 2
                              </label>
                              <input
                                type="text"
                                id="shippingAddress2"
                                name="shippingAddress2"
                                value={shippingAddress2}
                                onChange={(e) => setShippingAddress2(e.target.value)}
                              />
                            </div>
                          </div>

                          <div className="user-checkout-row">
                            <div className="user-checkout-col-50-1">
                              <label htmlFor="zipcode">
                                Zipcode <span className="user-checkout-required">*</span>
                              </label>
                              <input
                                type="number"
                                id="zipcode"
                                name="zipcode"
                                required
                                value={zipcode}
                                onChange={(e) => setZipcode(e.target.value)}
                              />
                            </div>
                            <div className="user-checkout-col-50-1">
                              <label htmlFor="city">
                                <i className="fa fa-institution"></i> City{" "}
                                <span className="user-checkout-required">*</span>
                              </label>
                              <input
                                type="text"
                                id="city"
                                name="city"
                                required
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                              />
                            </div>
                            <div className="user-checkout-col-50-1">
                              <label htmlFor="state">
                                State <span className="user-checkout-required">*</span>
                              </label>
                              <select
                                id="state"
                                name="state"
                                required
                                value={state}
                                onChange={(e) => setState(e.target.value)}
                              >
                                <option value="">Select State</option>
                                {malaysiaStates.map((state) => (
                                  <option key={state} value={state}>
                                    {state}
                                  </option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Order Comment */}
                  <div className="user-checkout-form-container" style={{ marginTop: '1.25rem' }}>
                    <h3>Order Comments</h3>
                    <div className="user-checkout-col-50" style={{ flex: '100%', minWidth: 'auto' }}>
                      <label htmlFor="orderComment">
                        <i className="fa fa-comment"></i> Remarks / Special Instructions
                      </label>
                      <textarea
                        id="orderComment"
                        name="orderComment"
                        className="user-checkout-comment-textarea"
                        rows="4"
                        placeholder="Any special requests or notes for your order..."
                        value={orderComment}
                        onChange={(e) => setOrderComment(e.target.value)}
                        maxLength={500}
                      />
                      <p className="user-checkout-comment-counter">{orderComment.length}/500</p>
                    </div>
                  </div>

                  {/* E-Invoice */}
                  <div className="user-checkout-form-container" style={{ marginTop: '1.25rem' }}>
                    <h3>E-Invoice</h3>
                        <div className="user-checkout-row">
                          <div className="user-checkout-col-50">
                            <label htmlFor="eInvoiceBuyerType">
                              <i className="fa fa-user"></i> Buyer Type <span style={{ color: 'red' }}>*</span>
                            </label>
                            <select
                              id="eInvoiceBuyerType"
                              name="eInvoiceBuyerType"
                              value={eInvoiceBuyerType}
                              onChange={(e) => {
                                setEInvoiceBuyerType(e.target.value);
                                setEInvoiceBRN("");
                              }}
                              required={eInvoiceRequested}
                            >
                              <option value="Individual">Individual</option>
                              <option value="Company">Company</option>
                            </select>
                          </div>
                          <div className="user-checkout-col-50">
                            <label htmlFor="eInvoiceName">
                              <i className="fa fa-id-card"></i> {eInvoiceBuyerType === 'Company' ? 'Company Name' : 'Full Name'} <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                              type="text"
                              id="eInvoiceName"
                              name="eInvoiceName"
                              value={eInvoiceName}
                              onChange={(e) => setEInvoiceName(e.target.value)}
                              required={eInvoiceRequested}
                              placeholder={eInvoiceBuyerType === 'Company' ? 'ABC Sdn Bhd' : 'As per NRIC'}
                            />
                          </div>
                        </div>

                        <div className="user-checkout-row">
                          <div className="user-checkout-col-50">
                            <label htmlFor="eInvoiceTIN">
                              <i className="fa fa-hashtag"></i> TIN <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                              type="text"
                              id="eInvoiceTIN"
                              name="eInvoiceTIN"
                              value={eInvoiceTIN}
                              onChange={(e) => setEInvoiceTIN(e.target.value)}
                              required={eInvoiceRequested}
                              placeholder="e.g. IG12345678900 / C12345678900"
                            />
                          </div>
                          <div className="user-checkout-col-50">
                            <label htmlFor="eInvoiceBRN">
                              <i className="fa fa-building"></i> {eInvoiceBuyerType === 'Company' ? 'Business Registration No.' : 'NRIC'} <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                              type="text"
                              id="eInvoiceBRN"
                              name="eInvoiceBRN"
                              value={eInvoiceBRN}
                              onChange={(e) => setEInvoiceBRN(e.target.value)}
                              required={eInvoiceRequested}
                              placeholder={eInvoiceBuyerType === 'Company' ? 'e.g. 202401012345' : 'e.g. 900101-01-1234'}
                            />
                          </div>
                        </div>

                        <div className="user-checkout-row">
                          <div className="user-checkout-col-50">
                            <label htmlFor="eInvoiceSSTNumber">
                              <i className="fa fa-file-invoice"></i> SST Registration No. <span style={{ color: '#777', fontWeight: 'normal' }}>(optional)</span>
                            </label>
                            <input
                              type="text"
                              id="eInvoiceSSTNumber"
                              name="eInvoiceSSTNumber"
                              value={eInvoiceSSTNumber}
                              onChange={(e) => setEInvoiceSSTNumber(e.target.value)}
                              placeholder="If SST-registered"
                            />
                          </div>
                          <div className="user-checkout-col-50">
                            <label htmlFor="eInvoicePhone">
                              <i className="fa fa-phone"></i> Contact Number <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                              type="tel"
                              id="eInvoicePhone"
                              name="eInvoicePhone"
                              value={eInvoicePhone}
                              onChange={(e) => setEInvoicePhone(e.target.value)}
                              required={eInvoiceRequested}
                              placeholder="e.g. 0123456789"
                            />
                          </div>
                        </div>

                        <div className="user-checkout-row">
                          <div className="user-checkout-col-50" style={{ flex: '100%', minWidth: 'auto' }}>
                            <label htmlFor="eInvoiceEmail">
                              <i className="fa fa-envelope"></i> Email <span style={{ color: 'red' }}>*</span>
                            </label>
                            <input
                              type="email"
                              id="eInvoiceEmail"
                              name="eInvoiceEmail"
                              value={eInvoiceEmail}
                              onChange={(e) => setEInvoiceEmail(e.target.value)}
                              required={eInvoiceRequested}
                              placeholder="For e-invoice delivery"
                            />
                          </div>
                        </div>

                        <div className="user-checkout-row">
                          <div className="user-checkout-col-50" style={{ flex: '100%', minWidth: 'auto' }}>
                            <label htmlFor="eInvoiceAddress">
                              <i className="fa fa-map-marker-alt"></i> Billing Address (for E-Invoice) <span style={{ color: 'red' }}>*</span>
                            </label>
                            <textarea
                              id="eInvoiceAddress"
                              name="eInvoiceAddress"
                              rows="3"
                              value={eInvoiceAddress}
                              onChange={(e) => setEInvoiceAddress(e.target.value)}
                              required={eInvoiceRequested}
                              placeholder="Full registered address"
                              className="user-checkout-comment-textarea"
                            />
                          </div>
                        </div>
                  </div>

                  {/* Terms & Conditions and Privacy Policy */}
                  <div className="user-checkout-agreement-section">
                    <div className="user-checkout-agreement-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={agreeTnC}
                          onChange={(e) => setAgreeTnC(e.target.checked)}
                        />
                        I have read and agree to the{" "}
                        <a href="/terms?tab=terms" target="_blank" rel="noopener noreferrer" className="user-checkout-agreement-link">
                          Terms and Conditions
                        </a>
                        <span className="user-checkout-required">*</span>
                      </label>
                    </div>
                    <div className="user-checkout-agreement-item">
                      <label>
                        <input
                          type="checkbox"
                          checked={agreePrivacy}
                          onChange={(e) => setAgreePrivacy(e.target.checked)}
                        />
                        I have read and agree to the{" "}
                        <a href="/terms?tab=privacy" target="_blank" rel="noopener noreferrer" className="user-checkout-agreement-link">
                          Privacy Policy
                        </a>
                        <span className="user-checkout-required">*</span>
                      </label>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="user-checkout-btn"
                    disabled={stockValidationErrors.length > 0 ||
                      pointsValidationErrors.length > 0 ||
                      cartCompositionError ||
                      isLoading}
                  >
                    {isLoading ? (
                      <span>
                        <i className="fa fa-spinner fa-spin" style={{ marginRight: '10px' }}></i>
                        Processing...
                      </span>
                    ) : (
                      "Place Order"
                    )}
                  </button>
                </form>
              </div>
            </div>

            {/* Cart Details Section */}
            <div className="user-checkout-col-25">
              <div className="user-checkout-form-container">
                <h4>
                  Cart{" "}
                  <span className="user-checkout-price">
                    <i className="fa fa-shopping-cart"></i> <b>{cart.length}</b>
                  </span>
                </h4>

                {/* Points Balance Display */}
                {userInfo && redeemableItems.length > 0 && (
                  <div style={{ padding: '10px 0', borderBottom: '1px solid #eee', marginBottom: '15px' }}>
                    <p style={{ margin: '0', fontSize: '0.9rem', color: '#666' }}>
                      Your Points Balance:
                      <span style={{ float: 'right', fontWeight: 'bold', color: '#0288d1' }}>
                        {pointsLoading ? (
                          <i className="fa fa-spinner fa-spin"></i>
                        ) : (
                          pointsBalance.toLocaleString()
                        )}
                      </span>
                    </p>
                  </div>
                )}

                {cart.map((product) => (
                  <div key={product.promotionProductId
                    ? `promo-${product.promotionProductId}`
                    : `${product.productID}-${product.selectedVariant?.variantId || 'default'}`} className="user-checkout-cart-item">
                    <p>
                      <span>
                        {product.name} x {product.quantity}
                      </span>
                      <span className="user-checkout-price">
                        {product.isRedeemable ? (
                          'RM0.00'
                        ) : (
                          (() => {
                            const ep = getEffectivePrice(product);
                            return `RM${(ep * product.quantity).toFixed(2)}`;
                          })()
                        )}
                      </span>
                    </p>
                    {product.selectedVariant && !product.isRedeemable && (
                      <div className="user-checkout-variant-detail">
                        {product.selectedVariant.variantValues?.map(
                          (variantValue) => {
                            const specValue = productDetails[
                              product.productID
                            ]?.specifications
                              ?.flatMap((s) => s.specificationValues || [])
                              .find(
                                (sv) =>
                                  sv.specificationValueId ===
                                  variantValue.specValueId
                              );
                            return specValue ? (
                              <div key={variantValue.specValueId}>
                                {
                                  productDetails[
                                    product.productID
                                  ]?.specifications?.find(
                                    (s) =>
                                      s.specificationId ===
                                      specValue.specificationId
                                  )?.name
                                }
                                : {specValue.value}
                              </div>
                            ) : null;
                          }
                        )}
                      </div>
                    )}
                  </div>
                ))}
                <hr />

                {/* Voucher Section - only for selling products */}
                <div className="user-checkout-voucher-section">
                  <label htmlFor="voucherCode">Voucher Code</label>
                  <div className="user-checkout-voucher-input-container">
                    <input
                      type="text"
                      id="voucherCode"
                      name="voucherCode"
                      className="user-checkout-voucher-input"
                      value={voucherCode}
                      onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
                      disabled={appliedVoucher !== null}
                    />
                    {!appliedVoucher ? (
                      <button
                        type="button"
                        onClick={validateVoucher}
                        className="user-checkout-voucher-btn"
                      >
                        Apply
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={removeVoucher}
                        className="user-checkout-voucher-btn remove"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  {voucherError && (
                    <div className="user-checkout-voucher-error">
                      <i className="fa fa-exclamation-circle"></i> {voucherError}
                    </div>
                  )}
                </div>

                {/* Order Summary */}
                <p>
                  Subtotal
                  <span className="user-checkout-price">
                    <b>RM{totalPrice.toFixed(2)}</b>
                  </span>
                </p>

                <p>
                  Shipping Fee
                  <span className="user-checkout-price" style={{ textAlign: "right", display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                    {isPickup ? (
                      <b>Free</b>
                    ) : (
                      <>
                        <span style={{ fontSize: "0.85em", color: "#666", fontWeight: "normal" }}>Waiting quote</span>
                        <b>RM 0.00</b>
                      </>
                    )}
                  </span>
                </p>

                {appliedVoucher && (
                  <p>
                    Discount
                    <span className="user-checkout-price user-checkout-discount">
                      -RM{discountAmount.toFixed(2)}
                    </span>
                  </p>
                )}

                {redeemableItems.length > 0 && (
                  <p>
                    Points Required
                    <span className="user-checkout-price" style={{ color: '#0288d1', fontWeight: 'bold' }}>
                      {totalPointsRequired} pts
                    </span>
                  </p>
                )}

                <p className="user-checkout-cart-total">
                  Total{" "}
                  <span className="user-checkout-price">
                    <b>
                      RM{finalPrice.toFixed(2)}
                      {redeemableItems.length > 0 && " + "}
                      {redeemableItems.length > 0 && (
                        <span style={{ color: '#0288d1' }}>{totalPointsRequired} pts</span>
                      )}
                    </b>
                  </span>
                </p>
              </div>
              <Link to="/cart">
                <button className="user-checkout-back-btn">Back to Cart</button>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Checkout;
